import { Component } from 'react';
import { getAdvisorErrorMessage } from './advisorErrorMessage';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { BsFillPersonFill } from 'react-icons/bs';
import { FaPeopleArrows, FaChild, FaBuilding, FaCoins } from 'react-icons/fa';
import WidgetPanel from 'src/components/dashboard/common/WidgetPanel';
import { layoutStyles } from 'src/components/dashboard/common/styles';
import {
  EmptyStateContent,
  NoPendingRequestsIllustration,
} from 'src/components/dashboard/pendingOrders/NoOrders';
import Loading from 'src/components/loading/Loading';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import SecondaryButton from 'src/design/components/button/SecondaryButton';
import TextInputWithError from 'src/design/components/textInput/TextInputWithError';
import Paragraph from 'src/design/components/bodyText/Paragraph';
import Icon from 'src/design/components/icon/Icon';
import { SubHeading } from 'src/design/styleguide/Headings';
import Text from 'src/design/styleguide/text/Text';
import { StickyHeader, Table } from 'src/design/components/table/Table';
import {
  breakpointMedium,
  breakpointSmall,
} from 'src/design/styleguide/common/breakpoints';
import { purple50 } from 'src/themes/brickx/colors';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import AdvisorService from 'scripts/services/AdvisorService';
import { errorAlert, successAlert } from 'scripts/redux/actions/alerts';
import Formatters from 'scripts/utilities/formattersV2';
import Regex from 'scripts/utilities/regex';
import { ACCOUNTTYPES } from 'src/settings/registration';

const ACCOUNT_TYPES = {
  [ACCOUNTTYPES.INDIVIDUAL]: { label: 'Individual', Icon: BsFillPersonFill },
  [ACCOUNTTYPES.MINOR]: { label: 'Minor', Icon: FaChild },
  [ACCOUNTTYPES.SMSF]: { label: 'SMSF', Icon: FaCoins },
  [ACCOUNTTYPES.TRUST]: { label: 'Trust', Icon: FaPeopleArrows },
  [ACCOUNTTYPES.CORPORATION]: { label: 'Company', Icon: FaBuilding },
};

const Panel = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  & > div:first-child {
    padding: 16px;
  }

  & > div:last-child {
    padding: 32px 16px;
  }

  ${breakpointSmall(`
    & > div:first-child {
      padding: 24px;
    }

    & > div:last-child {
      padding: 32px 24px;
    }
  `)}

  && input[class*='textInput__']:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
  }
`;

const PagePanels = styled.div`
  ${layoutStyles}
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
`;

const Intro = styled(Paragraph).attrs({ textSize: 'small' })`
  max-width: 650px;
  margin: 0;
`;

const SectionHeading = styled(SubHeading).attrs({
  as: 'h2',
  fontSize: '2x-small',
  align: 'left',
})`
  margin: 0 0 8px;
`;

const HelperText = styled(Text).attrs({ as: 'p', fontSize: '4x-small' })`
  margin: 0;
  color: var(--bx-color---grey-700);
`;

const RequestForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 12px;

  ${breakpointSmall(`
    flex-direction: row;
    align-items: flex-start;
  `)}

  ${breakpointMedium(`
    max-width: 560px;
  `)}
`;

const EmailField = styled.div`
  position: relative;
  flex: 1;
`;

const EmailInput = styled(TextInputWithError)`
  &&& {
    height: 50px;
    width: 100%;
    padding: 0 16px;
    border-radius: var(--bx-radius);
  }
`;

const AdvisorButton = styled(PrimaryButton)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-width: 120px;
    height: 50px;
    padding: 0 16px;
    border-radius: var(--bx-radius);
    white-space: nowrap;
  }

  &&:not([disabled]) {
    background-color: ${themeForAdvisorAccount.colors.primary};
  }

  &&[disabled],
  &&[disabled]:hover {
    background-color: var(--bx-color---grey-300);
    color: var(--bx-color---grey-500);
    transform: none;
  }

  ${breakpointSmall(`
    && {
      width: auto;
    }
  `)}
`;

const RequestNote = styled(HelperText)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
`;

const RequestInfoIcon = styled(Icon).attrs({
  type: Icon.types.info_outline,
})`
  flex: 0 0 auto;
  font-size: 18px;
  color: var(--bx-color---purple-600);
`;

const Divider = styled.div`
  height: 1px;
  margin: 20px 0;
  background: var(--bx-color---grey-200);
`;

const PendingRequestsTable = styled(Table)`
  width: 100%;
  max-width: 100%;
  min-width: 0;

  & > div {
    max-width: 100%;
  }

  table {
    border-collapse: collapse;
  }
`;

const TableHeading = styled(Text).attrs({
  as: 'th',
  fontSize: '4x-small',
})`
  padding: 12px 16px;
  border-bottom: 1px solid var(--bx-color---grey-200);
  color: var(--bx-color---grey-800);
  font-weight: normal;
  text-align: left;
  white-space: nowrap;
`;

const TableData = styled(Text).attrs({
  as: 'td',
  fontSize: '4x-small',
})`
  padding: 12px 16px;
  border-bottom: 1px solid var(--bx-color---grey-200);
  color: var(--bx-color---grey-800);
  vertical-align: middle;
  white-space: nowrap;

  ${PendingRequestsTable} tbody tr:last-child & {
    border-bottom: 0;
  }
`;

const Client = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
`;

const ClientName = styled.span`
  display: block;
  max-width: 160px;
  min-width: 0;
  overflow: hidden;
  color: var(--bx-color---grey-800);
  font-weight: bold;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClientEmail = styled.span`
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InitialsAvatar = styled.span`
  display: inline-flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${purple50};
  color: ${themeForAdvisorAccount.colors.primary};
  font-size: 12px;
  font-weight: bold;
`;

const AccountType = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  & > svg {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
    color: ${themeForAdvisorAccount.colors.primary};
  }
`;

const CancelButton = styled(SecondaryButton)`
  && {
    min-width: 54px;
  }
`;

const EmptyRequestsState = styled.div`
  margin-bottom: 24px;
`;

const TableNote = styled(RequestNote)`
  justify-content: center;
  border-top: 1px solid var(--bx-color---grey-200);
  padding-top: 16px;
  margin-top: 0;
`;

const getAccountType = (value) =>
  ACCOUNT_TYPES[String(value || '').toLowerCase()];

const getInitials = (name) => {
  if (!name) return '';

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

const formatDate = (value) => (value ? Formatters.time.date(value) : '-');

export const newestPendingRequestsFirst = (requests) =>
  requests.slice().sort((left, right) => {
    const leftUpdatedAt = left.updatedAt || 0;
    const rightUpdatedAt = right.updatedAt || 0;

    return (
      new Date(rightUpdatedAt).getTime() - new Date(leftUpdatedAt).getTime()
    );
  });

@connect(null, {
  errorAlert,
  successAlert,
})
export default class AdvisorPendingRequests extends Component {
  static propTypes = {
    errorAlert: PropTypes.func.isRequired,
    successAlert: PropTypes.func.isRequired,
  };

  state = {
    loading: true,
    clientEmail: '',
    touchedClientEmail: false,
    submitting: false,
    processingClientId: null,
    pendingRequests: [],
  };

  unmounted = false;
  loadRequestId = 0;

  componentDidMount() {
    this.unmounted = false;
    this.loadPendingRequests();
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.loadRequestId += 1;
  }

  isEmailValid = (email) => !!email && !!email.match(Regex.email);

  loadPendingRequests = async () => {
    const requestId = this.loadRequestId + 1;
    this.loadRequestId = requestId;
    try {
      const pendingRequests = await AdvisorService.getPendingRequests();
      if (this.unmounted || requestId !== this.loadRequestId) return;

      this.setState({
        loading: false,
        pendingRequests,
      });
    } catch (error) {
      if (this.unmounted || requestId !== this.loadRequestId) return;

      this.props.errorAlert(
        getAdvisorErrorMessage(
          error,
          'Unable to load pending connection requests.',
        ),
      );
      this.setState({ loading: false });
    }
  };

  onRequest = async (event) => {
    event.preventDefault();
    const clientEmail = this.state.clientEmail.trim();

    this.setState({ touchedClientEmail: true });
    if (this.state.submitting || !this.isEmailValid(clientEmail)) return;

    this.setState({ submitting: true });

    try {
      await AdvisorService.requestConnection(clientEmail);
      if (this.unmounted) return;

      this.props.successAlert('Connection request sent.');
      this.setState({
        clientEmail: '',
        touchedClientEmail: false,
        submitting: false,
      });
      await this.loadPendingRequests();
    } catch (error) {
      if (this.unmounted) return;

      this.props.errorAlert(
        getAdvisorErrorMessage(error, 'Unable to send connection request.'),
      );
      this.setState({ submitting: false });
    }
  };

  onCancel = async (clientId) => {
    if (this.state.processingClientId) return;

    this.setState({ processingClientId: clientId });
    try {
      await AdvisorService.cancelConnection(clientId);
      if (this.unmounted) return;

      this.props.successAlert('Pending adviser request cancelled.');
      this.setState({ processingClientId: null });
      await this.loadPendingRequests();
    } catch (error) {
      if (this.unmounted) return;

      this.props.errorAlert(
        getAdvisorErrorMessage(
          error,
          'Unable to cancel this connection request.',
        ),
      );
      this.setState({ processingClientId: null });
    }
  };

  renderRequest = (request) => {
    const clientName = request.name || '-';
    const clientEmail = request.email || '-';
    const accountType = getAccountType(request.accountType);
    const AccountTypeIcon = accountType && accountType.Icon;
    const { processingClientId } = this.state;

    return (
      <tr key={request.clientId}>
        <StickyHeader>
          <TableData>
            <Client>
              <InitialsAvatar>{getInitials(clientName)}</InitialsAvatar>
              <ClientName>{clientName}</ClientName>
            </Client>
          </TableData>
        </StickyHeader>
        <TableData>
          <ClientEmail>{clientEmail}</ClientEmail>
        </TableData>
        <TableData>
          {accountType ? (
            <AccountType>
              <AccountTypeIcon aria-hidden="true" />
              {accountType.label}
            </AccountType>
          ) : (
            '-'
          )}
        </TableData>
        <TableData>{formatDate(request.updatedAt)}</TableData>
        <TableData>
          <CancelButton
            size={SecondaryButton.sizes.EXTRA_SMALL}
            color={SecondaryButton.colors.PRIMARY}
            onClick={() => this.onCancel(request.clientId)}
            disabled={!!processingClientId}
          >
            {processingClientId === request.clientId
              ? 'Cancelling...'
              : 'Cancel'}
          </CancelButton>
        </TableData>
      </tr>
    );
  };

  render() {
    const {
      loading,
      clientEmail,
      touchedClientEmail,
      submitting,
      pendingRequests,
    } = this.state;
    const emailInvalid =
      touchedClientEmail && !this.isEmailValid(clientEmail.trim());

    if (loading) {
      return <Loading />;
    }

    return (
      <PagePanels testRef="advisor-pending-page">
        <Panel title="Client Connections">
          <Intro>
            Connect with existing BrickX clients and manage requests awaiting
            approval.
          </Intro>
          <Divider />

          <section aria-labelledby="request-connection-heading">
            <SectionHeading id="request-connection-heading">
              Request Connection
            </SectionHeading>
            <HelperText>
              Enter the email address associated with your client&apos;s BrickX
              account.
            </HelperText>
            <RequestForm onSubmit={this.onRequest}>
              <EmailField>
                <EmailInput
                  forId="advisor-client-email"
                  type="email"
                  value={clientEmail}
                  onChange={(value) => this.setState({ clientEmail: value })}
                  onBlur={() => this.setState({ touchedClientEmail: true })}
                  placeholder="client@example.com"
                  aria-label="Client email address"
                  error={emailInvalid && 'Please enter a valid email address.'}
                />
              </EmailField>
              <AdvisorButton
                type="submit"
                color={PrimaryButton.colors.PURPLE}
                size={PrimaryButton.sizes.EXTRA_SMALL}
                disabled={!this.isEmailValid(clientEmail.trim()) || submitting}
              >
                {submitting ? 'Sending...' : 'Send Request'}
              </AdvisorButton>
            </RequestForm>
            <RequestNote>
              <RequestInfoIcon aria-hidden="true" />
              We&apos;ll email the client to approve your connection request.
            </RequestNote>
          </section>
        </Panel>

        <Panel title="Pending Requests">
          {pendingRequests.length ? (
            <PendingRequestsTable withStickyHeader centeredRows>
              <thead>
                <tr>
                  <StickyHeader>
                    <TableHeading>Client</TableHeading>
                  </StickyHeader>
                  <TableHeading>Email</TableHeading>
                  <TableHeading>Account Type</TableHeading>
                  <TableHeading>Requested</TableHeading>
                  <TableHeading />
                </tr>
              </thead>
              <tbody>
                {newestPendingRequestsFirst(pendingRequests).map(
                  this.renderRequest,
                )}
              </tbody>
            </PendingRequestsTable>
          ) : (
            <EmptyRequestsState>
              <EmptyStateContent
                message="No pending connection requests at the moment."
                illustration={<NoPendingRequestsIllustration />}
              />
            </EmptyRequestsState>
          )}
          <TableNote>
            <RequestInfoIcon aria-hidden="true" />
            Requests remain here until the client approves them or you cancel
            them.
          </TableNote>
        </Panel>
      </PagePanels>
    );
  }
}
